// src/pages/reports/ReportsCallbacks.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  TagLeftIcon,
  TagLabel,
  IconButton,
  Link as ChakraLink,
  Tooltip,
  Spinner,
  Switch,
  Select,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { PhoneIcon, CalendarIcon, ExternalLinkIcon, TimeIcon, CloseIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import {
  collection,
  onSnapshot,
  getFirestore,
  Timestamp,
  query,
  doc,
  addDoc,
  serverTimestamp,
  updateDoc,
  where,
  arrayUnion,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged, getIdTokenResult } from 'firebase/auth';

import { MENU } from '../../menu.config';
import AttemptDrawer from './components/AttemptDrawer.jsx';

// -----------------------------------------------------------------------------
// Constants / Config
// -----------------------------------------------------------------------------

const TRACKED_STATUSES = ['REAPP', 'PTA', 'BO', 'NCI', 'CPTA'];

// If you already export your DNC reasons from a shared module, swap this array for that import.
const DNC_REASONS = [
  'Requested no contact',
  'Wrong person / number',
  'Duplicate lead',
  'Deceased',
  'Abusive / Threatening',
  'Other',
];

const OUTCOME_PRESETS = [
  { key: 'NO_ANSWER', label: 'No answer', nextMs: 2 * 60 * 60 * 1000 }, // +2h
  { key: 'BUSY', label: 'Busy', nextMs: 60 * 60 * 1000 }, // +1h
  { key: 'VM_LEFT', label: 'Voicemail left', nextMs: 24 * 60 * 60 * 1000 }, // +24h
  {
    key: 'CALL_TODAY_PM',
    label: 'Today 15:00',
    fixedTime: (now) => new Date(now.getFullYear(), now.getMonth(), now.getDate(), 15, 0, 0, 0),
  },
  {
    key: 'CALL_TOMORROW_10',
    label: 'Tomorrow 10:00',
    fixedTime: (now) => {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      t.setHours(10, 0, 0, 0);
      return t;
    },
  },
];

const db = getFirestore();

export default function ReportsCallbacks() {
  const toast = useToast();

  // Use the named export MENU as the source of truth for routes
  const salesHref = useMemo(() => {
    const root = Array.isArray(MENU) ? MENU.find((g) => g.id === 'reports') : null;
    const child = root?.items?.find((i) => i.id === 'reports.sales');
    return child?.href || '/reports/sales';
  }, []);

  const [orgId, setOrgId] = useState('');
  const [user, setUser] = useState(null);

  // UI & filters
  const [search, setSearch] = useState('');
  const [hideNoDue, setHideNoDue] = useState(false);

  // Drawer + selection
  const drawer = useDisclosure();
  const [selected, setSelected] = useState(null);

  // Attempt form fields (kept here to avoid logic move)
  const [attemptNotes, setAttemptNotes] = useState('');
  const [attemptReason, setAttemptReason] = useState('');
  const [nextCallDate, setNextCallDate] = useState(null);

  // Reschedule modal
  const resModal = useDisclosure();
  const [rescheduleAt, setRescheduleAt] = useState('');

  // DNC modal
  const dncModal = useDisclosure();
  const [dncReason, setDncReason] = useState('');
  const [dncNote, setDncNote] = useState('');

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Auth & org claims
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setOrgId('');
        return;
      }
      setUser(u);
      const token = await getIdTokenResult(u, true);
      const claims = token?.claims || {};
      const oid = claims.orgId || '';
      setOrgId(oid);
    });
    return () => unsub();
  }, []);

  // Load queue
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);

    const q = query(
      collection(db, 'organizations', orgId, 'leads'),
      where('status', 'in', TRACKED_STATUSES)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((docSnap) => {
          const ld = { id: docSnap.id, ...docSnap.data() };
          if (ld.isDead) return; // DNC / archived from Call Again
          ld.attemptCount = ld.attemptCount || 0;
          rows.push(ld);
        });
        setLeads(rows);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast({
          status: 'error',
          title: 'Error loading Call Again list',
          description: err.message,
        });
        setLoading(false);
      }
    );

    return () => unsub();
  }, [orgId, toast]);

  // Helpers
  const getDueDate = (ld) => {
    const v = ld?.nextAction?.dueAt || ld?.lastCallbackNextCallDate || null;
    if (!v) return null;
    try {
      return v.toDate ? v.toDate() : new Date(v);
    } catch {
      return null;
    }
  };

  const displayName = (ld) => {
    if (!ld) return 'No name';
    const full = (ld.customerFullName ?? ld.customerName ?? ld.name ?? '').toString().trim();
       if (full) return full;
    const fn = (ld.customerFirstName ?? ld.firstName ?? '').toString().trim();
    const ln = (ld.customerLastName ?? ld.lastName ?? '').toString().trim();
    const built = [fn, ln].filter(Boolean).join(' ');
    if (built) return built;
    const contact = (ld.contact?.name ?? '').toString().trim();
    return contact || 'No name';
  };

  const displayPhone = (ld) =>
    ld?.phone ||
    ld?.customerPhone ||
    ld?.mobile ||
    ld?.tel ||
    ld?.contact?.phone ||
    ld?.phones?.primary ||
    '—';

  const renderTel = (p) => {
    if (!p || p === '—') return <Text as="span">—</Text>;
    const href = 'tel:' + p.replace(/[^+\d]/g, '');
    return (
      <ChakraLink href={href} color="teal.600">
        {p}
      </ChakraLink>
    );
  };

  const renderDueTag = (ld) => {
    const d = getDueDate(ld);
    if (!d) return <Tag size="sm">No due</Tag>;

    const now = new Date();
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
    const dueMs = d.getTime();
    const isToday = dueMs >= today0 && dueMs < today0 + 24 * 60 * 60 * 1000;

    return (
      <Tag size="sm" colorScheme={dueMs < now.getTime() ? 'red' : isToday ? 'yellow' : 'gray'}>
        <TagLeftIcon as={TimeIcon} />
        <TagLabel>{isToday ? 'Today' : d.toLocaleString()}</TagLabel>
      </Tag>
    );
  };

  // Selection helpers
  const openDrawerForLead = async (ld) => {
    setSelected(ld || null);
    setRescheduleAt('');
    drawer.onOpen();
  };

  const releaseLock = async (leadId) => {
    if (!orgId || !leadId) return;
    try {
      await updateDoc(doc(db, 'organizations', orgId, 'leads', leadId), { lock: null });
    } catch (e) {
      console.error(e);
    }
  };

  const saveAttempt = async () => {
    if (!orgId || !selected?.id) return;
    try {
      const next = nextCallDate ? Timestamp.fromDate(nextCallDate) : null;

      await addDoc(collection(db, 'organizations', orgId, 'leads', selected.id, 'callbacks'), {
        at: serverTimestamp(),
        byUid: user?.uid || null,
        byName: user?.displayName || user?.email || null,
        outcome: attemptReason || null,
        reasonCode: attemptReason || null,
        notes: (attemptNotes || '').trim() || null,
        nextCallDate: next,
      });

      await updateDoc(doc(db, 'organizations', orgId, 'leads', selected.id), {
        attemptCount: (selected.attemptCount || 0) + 1,
        lastCalledAt: serverTimestamp(),
        lastCallbackNextCallDate: next,
        nextAction: { dueAt: next },
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      toast({ status: 'success', title: 'Attempt saved' });
      await releaseLock(selected.id);
      drawer.onClose();
      setSelected(null);
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Failed to save attempt', description: e.message });
    }
  };

  const saveReschedule = async () => {
    try {
      const oldAppointment = selected?.appointment || null;
      const newAppointment = rescheduleAt || '';

      if (!newAppointment) {
        toast({ status: 'warning', title: 'Pick a new appointment date/time' });
        return;
      }

      await updateDoc(doc(db, 'organizations', orgId, 'leads', selected.id), {
        appointment: newAppointment || '',
        status: 'NEW',
        salespersonId: '',
        salespersonName: '',
        nextAction: { dueAt: null },
        lastCallbackNextCallDate: null,
        sellerConfirmed: false,
        sellerConfirmedAt: null,
        apptStartAt: null,
        apptEndAt: null,
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'organizations', orgId, 'leads', selected.id, 'callbacks'), {
        at: serverTimestamp(),
        byUid: user?.uid || null,
        byName: user?.displayName || user?.email || null,
        outcome: 'RESCHEDULED',
        oldAppointment: oldAppointment || null,
        newAppointment: newAppointment || null,
      });

      toast({ status: 'success', title: 'Description updated', description: 'Lead moved to NEW' });
      await releaseLock(selected.id);
      resModal.onClose();
      setSelected(null);
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Failed to reschedule', description: e.message });
    }
  };

  const saveDnc = async () => {
    try {
      if (!orgId || !selected?.id) return;

      const reason = (dncReason || '').trim() || 'Requested no contact';
      const note = (dncNote || '').trim() || null;

      // callbacks log entry
      await addDoc(collection(db, 'organizations', orgId, 'leads', selected.id, 'callbacks'), {
        at: serverTimestamp(),
        byUid: user?.uid || null,
        byName: user?.displayName || user?.email || null,
        outcome: 'DNC',
        reason,
        notes: note,
      });

      // lead update
      await updateDoc(doc(db, 'organizations', orgId, 'leads', selected.id), {
        status: 'ARCHIVE_DNC',
        isDead: true,
        doNotContact: true,
        dnc: {
          reason,
          note,
          at: serverTimestamp(),
          byUid: user?.uid || null,
          byName: user?.displayName || user?.email || null,
        },
        nextAction: { dueAt: null },
        lastCallbackNextCallDate: null,
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        // IMPORTANT: Firestore does not allow serverTimestamp() inside arrayUnion().
        // Use a client timestamp for the entry itself, while keeping updatedAt as serverTimestamp.
        statusHistory: arrayUnion({
          at: Timestamp.now(),
          byUid: user?.uid || null,
          byName: user?.displayName || user?.email || null,
          from: selected?.status || null,
          to: 'ARCHIVE_DNC',
          note: reason,
        }),
      });

      toast({ status: 'success', title: 'Marked as Do Not Contact' });
      dncModal.onClose();
      setDncReason('');
      setDncNote('');
      setSelected(null);
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'Failed to mark DNC', description: e.message });
    }
  };

  // Filtering
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    const list = Array.isArray(leads) ? leads : [];
    return list.filter((ld) => {
      if (hideNoDue && !getDueDate(ld)) return false;
      if (!q) return true;
      const blob = JSON.stringify(ld || {}).toLowerCase();
      return blob.includes(q);
    });
  }, [leads, search, hideNoDue]);

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg" color="blue.600">
          Call Again (REAPP / PTA / BO / NCI / CPTA)
        </Heading>
        <HStack>
          <Input
            placeholder="Search…"
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            width="260px"
          />
          <HStack>
            <Text fontSize="sm">Hide “No due”</Text>
            <Switch isChecked={hideNoDue} onChange={(e) => setHideNoDue(e.target.checked)} />
          </HStack>
          <Button as={RouterLink} to={salesHref} size="sm" leftIcon={<ExternalLinkIcon />}>
            Sales Reports
          </Button>
        </HStack>
      </HStack>

      <Text color="gray.600" mb={4}>
        This report lists all leads marked as REAPP, PTA, BO, NCI, CPTA. Use it to try and book them
        in for a new appointment.
      </Text>

      <Box borderWidth="1px" borderRadius="lg" overflowX="auto" bg="white">
        {loading ? (
          <HStack p={6} justify="center">
            <Spinner />
            <Text>Loading…</Text>
          </HStack>
        ) : (
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Lead</Th>
                <Th>Phone</Th>
                <Th>Email</Th>
                <Th>Attempts</Th>
                <Th>Due</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((ld) => {
                const email = ld?.email || ld?.customerEmail || '—';
                return (
                  <Tr key={ld.id} _hover={{ bg: 'gray.50' }}>
                    <Td>{displayName(ld)}</Td>
                    <Td>{renderTel(displayPhone(ld))}</Td>
                    <Td>
                      {email !== '—' ? (
                        <ChakraLink href={`mailto:${email}`} color="teal.600">
                          {email}
                        </ChakraLink>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      <Tag size="sm">
                        <TagLabel>{ld.attemptCount || 0}</TagLabel>
                      </Tag>
                    </Td>
                    <Td>{renderDueTag(ld)}</Td>
                    <Td textAlign="right">
                      <HStack justify="flex-end" spacing={2}>
                        <Tooltip label="DNC / Archive">
                          <IconButton
                            aria-label="Do Not Contact"
                            icon={<CloseIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => {
                              setSelected(ld);
                              setDncReason('');
                              setDncNote('');
                              dncModal.onOpen();
                            }}
                          />
                        </Tooltip>
                        <Tooltip label="Call now">
                          <IconButton
                            aria-label="Call"
                            icon={<PhoneIcon />}
                            size="sm"
                            onClick={() => openDrawerForLead(ld)}
                          />
                        </Tooltip>
                        <Tooltip label="Reschedule">
                          <IconButton
                            aria-label="Reschedule"
                            icon={<CalendarIcon />}
                            size="sm"
                            onClick={() => {
                              setSelected(ld);
                              setRescheduleAt('');
                              resModal.onOpen();
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Lead Drawer */}
      <AttemptDrawer
        isOpen={drawer.isOpen}
        onClose={async () => {
          if (selected?.id) await releaseLock(selected.id);
          drawer.onClose();
          setSelected(null);
        }}
        name={displayName(selected || {})}
        phone={displayPhone(selected || {})}
        email={selected?.email || selected?.customerEmail || '—'}
        attemptReason={attemptReason}
        setAttemptReason={setAttemptReason}
        attemptNotes={attemptNotes}
        setAttemptNotes={setAttemptNotes}
        nextCallDate={nextCallDate}
        setNextCallDate={setNextCallDate}
        outcomePresets={OUTCOME_PRESETS}
        onSaveAttempt={saveAttempt}
      />

      {/* Reschedule Modal */}
      <Modal isOpen={resModal.isOpen} onClose={resModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reschedule Appointment</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Input
                type="datetime-local"
                value={rescheduleAt}
                onChange={(e) => setRescheduleAt(e.target.value)}
                size="sm"
              />
              <Text fontSize="sm" color="gray.600">
                Lead: {displayName(selected || {})}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={resModal.onClose}>
                Cancel
              </Button>
              <Button colorScheme="teal" leftIcon={<CalendarIcon />} onClick={saveReschedule}>
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* DNC / Archive Modal */}
      <Modal isOpen={dncModal.isOpen} onClose={dncModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark as Do Not Contact</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Select
                placeholder="Select a reason"
                size="sm"
                value={dncReason}
                onChange={(e) => setDncReason(e.target.value)}
              >
                {DNC_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
              <Input
                placeholder="Notes (optional)"
                size="sm"
                value={dncNote}
                onChange={(e) => setDncNote(e.target.value)}
              />
              <Text fontSize="sm" color="gray.600">
                Lead: {displayName(selected || {})}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={dncModal.onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" leftIcon={<CloseIcon />} onClick={saveDnc}>
                Confirm DNC
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
